import React, { useEffect, useMemo, useState } from "react";
import {
  View,
  Text,
  ActivityIndicator,
  StyleSheet,
  ScrollView,
} from "react-native";
import {
  useNavigation,
  useRoute,
  RouteProp,
} from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { RootStackParamList } from "../navigation";
import { Opportunity } from "../types";
import { getOpportunities } from "../services/parseService";
import { useTheme } from "../contexts/ThemeContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import OpportunityDetail from "../components/OpportunityDetail";
import CrawlableLink from "../components/CrawlableLink";
import { getOpportunitySeed } from "../utils/seoUtils";

type OpportunityRouteProp = RouteProp<RootStackParamList, "Opportunity">;

/**
 * Dedicated page for /opportunity/:opportunityId.
 *
 * Why this screen exists (SEO): a direct/crawler load of an opportunity URL
 * used to be force-fed into the Home screen, collapsing the address bar to "/"
 * and rendering the homepage filtered to a single card — a thin, near-duplicate
 * page (soft 404). This screen instead renders the full OpportunityDetail inline
 * at a stable /opportunity/:id URL, so the rendered DOM Google indexes is rich
 * and unique per opportunity. It hydrates synchronously from
 * window.__INITIAL_DATA__ (seeded by scripts/generate-static.js for each static
 * opportunity page) so crawlers see content without waiting on a network round
 * trip, and falls back to a live fetch for in-session navigations.
 */

const getSeedOpportunities = (): Opportunity[] =>
  typeof window !== "undefined" &&
  Array.isArray((window as any).__INITIAL_DATA__)
    ? ((window as any).__INITIAL_DATA__ as Opportunity[])
    : [];

type RelatedOpportunity = { id: string; title: string; organization: string };

const primaryType = (opp: Opportunity): string =>
  (Array.isArray(opp.type) ? opp.type[0] || "" : String(opp.type || "").split(",")[0]).trim();

// Client-side equivalent of relatedOpps() in scripts/generate-static.js: up to
// five other opportunities sharing this one's primary type, falling back to any
// others when the type is unique. Only used for in-session navigations; a
// statically generated page uses the build-time seed so the rendered list
// matches the crawled HTML exactly.
const pickRelated = (
  opp: Opportunity,
  all: Opportunity[]
): RelatedOpportunity[] => {
  const type = primaryType(opp);
  const others = all.filter((o) => o.id !== opp.id);
  const sameType = others.filter((o) => primaryType(o) === type);
  return (sameType.length ? sameType : others).slice(0, 5).map((o) => ({
    id: o.id,
    title: o.title,
    organization: o.organization || "",
  }));
};

const OpportunityScreen: React.FC = () => {
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const route = useRoute<OpportunityRouteProp>();
  const { theme } = useTheme();
  const opportunityId = route.params?.opportunityId;

  const seedMatch = useMemo(() => {
    if (!opportunityId) return null;
    const seed = getSeedOpportunities();
    return (
      seed.find((o) => o && o.id === opportunityId) ||
      // A statically-generated /opportunity/:id page seeds exactly this one
      // opportunity, so fall back to it when the id doesn't match exactly
      // (e.g. a trailing slash on the path param).
      (seed.length === 1 ? seed[0] : null)
    );
  }, [opportunityId]);

  const [opportunity, setOpportunity] = useState<Opportunity | null>(seedMatch);
  const [loading, setLoading] = useState<boolean>(!seedMatch);
  // The same "Related opportunities" set the static HTML lists. Rendering it
  // here is what keeps those internal links in the DOM Google indexes —
  // hydration wipes the build-time markup, and without this a detail page ends
  // up with a single outbound link (the breadcrumb) and no link graph between
  // opportunities.
  const [related, setRelated] = useState<RelatedOpportunity[]>(
    () => getOpportunitySeed(opportunityId || "")?.related || []
  );

  useEffect(() => {
    let cancelled = false;

    if (!opportunityId) {
      setOpportunity(null);
      setLoading(false);
      return;
    }
    if (seedMatch) {
      setOpportunity(seedMatch);
      setRelated(getOpportunitySeed(opportunityId)?.related || []);
      setLoading(false);
      return;
    }

    // Not in the SSR seed (e.g. in-session navigation) — fetch and resolve.
    setLoading(true);
    getOpportunities(500)
      .then((all: Opportunity[]) => {
        if (cancelled) return;
        const list = all || [];
        const match = list.find((o) => o.id === opportunityId) || null;
        setOpportunity(match);
        setRelated(match ? pickRelated(match, list) : []);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [opportunityId, seedMatch]);

  // React Navigation sets document.title from the screen (defaulting to the
  // route name "Opportunity"). Override it with the opportunity title so the
  // tab/title matches the canonical per-opportunity SEO title that
  // OpportunityDetail applies for description/canonical/JSON-LD.
  useEffect(() => {
    if (opportunity) {
      // Match the build-time rule: drop the brand suffix on long titles to stay
      // within Google's ~60-char display limit.
      navigation.setOptions({
        title:
          opportunity.title.length > 45
            ? opportunity.title
            : `${opportunity.title} | BC STEM Opportunities`,
      });
    }
  }, [opportunity, navigation]);

  const bg = theme === "dark" ? "#18181B" : "#FAFAFA";
  const text = theme === "dark" ? "#F2F2F2" : "#18181B";
  const subText = theme === "dark" ? "#A1A1AA" : "#71717A";

  const goHome = () => navigation.navigate("Home", {});

  return (
    <View style={[styles.root, { backgroundColor: bg }]}>
      <Header />

      {opportunity && (
        <View style={styles.breadcrumb}>
          <CrawlableLink
            to="/"
            onPress={goHome}
            style={[styles.breadcrumbLink, { color: subText }]}
          >
            BC STEM Opportunities
          </CrawlableLink>
          <Text style={[styles.breadcrumbSep, { color: subText }]}> / </Text>
          <Text
            style={[styles.breadcrumbCurrent, { color: text }]}
            numberOfLines={1}
          >
            {opportunity.title}
          </Text>
        </View>
      )}

      <View style={styles.body}>
        {loading ? (
          <View style={styles.center}>
            <ActivityIndicator size="large" color={text} />
          </View>
        ) : opportunity ? (
          <ScrollView contentContainerStyle={styles.scrollBody}>
            <OpportunityDetail
              opportunity={opportunity}
              onClose={goHome}
              isModal={false}
            />
            {related.length > 0 && (
              <View style={styles.related}>
                <Text
                  accessibilityRole="header"
                  aria-level={2}
                  style={[styles.relatedHeading, { color: text }]}
                >
                  Related opportunities
                </Text>
                {related.map((r) => (
                  <CrawlableLink
                    key={r.id}
                    to={`/opportunity/${r.id}`}
                    onPress={() =>
                      navigation.navigate("Opportunity", {
                        opportunityId: r.id,
                      })
                    }
                    style={[styles.relatedLink, { color: text }]}
                  >
                    {r.title}
                    {r.organization ? (
                      <Text style={{ color: subText }}> — {r.organization}</Text>
                    ) : null}
                  </CrawlableLink>
                ))}
              </View>
            )}
            <Footer />
          </ScrollView>
        ) : (
          <View style={styles.center}>
            <Text
              accessibilityRole="header"
              aria-level={1}
              style={[styles.notFoundTitle, { color: text }]}
            >
              Opportunity not found
            </Text>
            <Text style={[styles.notFoundBody, { color: subText }]}>
              This opportunity may have been removed or the link is incorrect.
            </Text>
            <CrawlableLink
              to="/"
              onPress={goHome}
              style={[styles.notFoundLink, { color: text }]}
            >
              ← Browse all BC STEM opportunities
            </CrawlableLink>
          </View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  breadcrumb: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 24,
    paddingVertical: 10,
    maxWidth: 1200,
    width: "100%",
    alignSelf: "center",
  },
  breadcrumbLink: {
    fontSize: 13,
    fontFamily: "Inter-Medium",
  },
  breadcrumbSep: {
    fontSize: 13,
  },
  breadcrumbCurrent: {
    fontSize: 13,
    fontFamily: "Inter-SemiBold",
    flexShrink: 1,
  },
  body: {
    flex: 1,
  },
  scrollBody: {
    flexGrow: 1,
  },
  related: {
    maxWidth: 1200,
    width: "100%",
    alignSelf: "center",
    paddingHorizontal: 24,
    paddingTop: 8,
    paddingBottom: 32,
    gap: 8,
  },
  relatedHeading: {
    fontSize: 18,
    fontFamily: "Inter-Bold",
    marginBottom: 4,
  },
  relatedLink: {
    fontSize: 15,
    fontFamily: "Inter-Medium",
    lineHeight: 22,
  },
  center: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 32,
  },
  notFoundTitle: {
    fontSize: 22,
    fontFamily: "Inter-Bold",
    marginBottom: 8,
  },
  notFoundBody: {
    fontSize: 15,
    fontFamily: "Inter-Regular",
    marginBottom: 16,
    textAlign: "center",
  },
  notFoundLink: {
    fontSize: 15,
    fontFamily: "Inter-SemiBold",
  },
});

export default OpportunityScreen;
